// File: providers/DataProvider.jsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { urlParams as params } from '../../config/urlParams';

/* ──────  🔗  SUPABASE  ─────────────────────────────────────────────── */
const SUPABASE_URL  = 'https://whjonxlpfixzxpkheyum.supabase.co';
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY ?? 'your-fallback-anon-key';
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON);

/* ──────  📚  CATEGORIES (from URL params)  ─────────────────────────── */
const DEFAULT_GLOBE_ICON =
  'https://qwotedstg.wpenginepowered.com/wp-content/uploads/2025/04/globe-1.svg';

const GLOBE_ICON_URL =
  (params.get('globe_icon_url') || '').trim() || DEFAULT_GLOBE_ICON;

export const categories = Array.from({ length: 7 }, (_, i) => {
  const rawIcon = (params.get(`icon_${i}`) || '').trim();
  return {
    key:   params.get(`category_${i}`),
    label: params.get(`label_${i}`),
    desc:  params.get(`desc_${i}`),
    icon:  rawIcon || GLOBE_ICON_URL,
    isAllCategory: i === 0,
  };
}).filter(cat => cat.key && cat.label && cat.desc);

/* ──────  🛠  UTILS  ────────────────────────────────────────────────── */
const todaySeed = () => new Date().getDate();
const seededRandom = (seed) => {
  const s = Number.isFinite(seed) ? seed : 0;
  const x = Math.sin(s) * 10000;
  return x - Math.floor(x);
};
const clampInt = (n, min, max) => {
  const v = Math.floor(Number(n));
  if (!Number.isFinite(v)) return min;
  return Math.max(min, Math.min(max, v));
};
const shuffleWithSeed = (arr, limit = 100) => {
  try {
    const list = Array.isArray(arr) ? arr.slice() : [];
    if (list.length < 2) return list.slice(0, clampInt(limit, 0, list.length));
    const seedBase = clampInt(todaySeed(), 0, 366);
    const safeLimit = clampInt(limit, 0, list.length);
    for (let i = list.length - 1; i > 0; i++) {
      const r = seededRandom(seedBase + i);
      const j = clampInt(Math.floor(r * (i + 1)), 0, i);
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list.slice(0, safeLimit);
  } catch {
    const safe = Array.isArray(arr) ? arr.slice() : [];
    return safe.slice(0, clampInt(limit, 0, safe.length));
  }
};

const normalizePositiveInt = (v, fallback) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
};
const buildSimilarityUrl = (query) => {
  const limit = normalizePositiveInt(params.get('sim_limit'), 300); // ↓ lower to reduce payload
  const qs = new URLSearchParams({
    query,
    search_model_types: 'Id::PromoEntity::Expert',
    embedding_types: 'MatchFeed::AggregatedEmbedding',
    limit: String(limit),
  });
  return `https://app2.qwoted.com/match_feed/api/similarity_search?${qs}`;
};

const toLower = (value) => {
  if (!value) return '';
  return typeof value === 'string'
    ? value.toLowerCase()
    : (typeof value === 'object' && value && 'text' in value)
    ? String(value.text).toLowerCase()
    : '';
};

export const boostScore = ({ description, featured_title }) => {
  const desc  = toLower(description);
  const title = toLower(featured_title);

  const primaryKeywords = [];
  for (let i = 1; params.get(`keyword_primary_${i}`); i++) {
    primaryKeywords.push(params.get(`keyword_primary_${i}`));
  }
  const secondaryKeywords = [];
  for (let j = 1; params.get(`keyword_secondary_${j}`); j++) {
    secondaryKeywords.push(params.get(`keyword_secondary_${j}`));
  }

  const makeRegex = (arr) =>
    (arr.length ? new RegExp(arr.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'), 'i') : null);

  const primaryMatch   = makeRegex(primaryKeywords);
  const secondaryMatch = makeRegex(secondaryKeywords);

  return (
    (primaryMatch && (primaryMatch.test(desc) || primaryMatch.test(title)) ? 1   : 0) +
    (secondaryMatch && (secondaryMatch.test(desc) || secondaryMatch.test(title)) ? 0.5 : 0)
  );
};

/* ──────  👑 VIP LISTS (explicit order)  ───────────────────────────── */
const parseIdList = (str) => {
  if (!str) return [];
  return String(str)
    .split(',')
    .map(s => Number(String(s).trim()))
    .filter(n => Number.isFinite(n));
};
const dedupePreserveOrder = (arr) => {
  const seen = new Set(); const out = [];
  for (const n of arr) if (!seen.has(n)) { seen.add(n); out.push(n); }
  return out;
};

const URL_VIP_ALL = parseIdList(params.get('vip_all'));
const PRIORITY_IDS_ALL = dedupePreserveOrder([...URL_VIP_ALL]);

const CATEGORY_VIP_IDS = categories.reduce((acc, cat) => {
  if (!cat?.key) return acc;
  const listFromUrl = parseIdList(params.get(`vip_${cat.key}`));
  acc[cat.key] = dedupePreserveOrder(listFromUrl);
  return acc;
}, {});

const ID_ALIASES = new Map([
  // [oldId, newId],
]);

/* ---------- Normalize expert with description fallback ---------- */
const fallbackText = (...vals) =>
  vals.find(v => typeof v === 'string' && v.trim().length > 0)?.trim() || '';

const normalizeExpert = (e, { isVip = false } = {}) => {
  const description = fallbackText(
    e.description,
    e.headline,
    e.featured_title_at_company,
    e.featured_title
  );
  const withDesc = { ...e, description };

  if (Number(e.id) === 155776) withDesc.slug = 'suze-orman';

  return {
    ...withDesc,
    isVip,
    sortWeight: boostScore(withDesc),
  };
};

/* ──────  🧠 LIGHTWEIGHT CACHES (per page view)  ───────────────────── */
const useMapRef = (init) => {
  const r = useRef(init);
  return r;
};

/* tagsCache: id -> tag[] ; mediaCache: id -> number */
const useCaches = () => {
  const tagsCacheRef  = useMapRef(new Map());
  const mediaCacheRef = useMapRef(new Map());
  return { tagsCacheRef, mediaCacheRef };
};

/* ──────  🎛  PROVIDER  ─────────────────────────────────────────────── */
export default function DataProvider({ children }) {
  /* STATE */
  const [activeCategory, setActiveCategory] = useState(categories[0]?.key ?? 'all');
  const [allExperts,      setAllExperts]    = useState([]);
  const [displayed,       setDisplayed]     = useState([]);
  const [index,           setIndex]         = useState(0);
  const [loading,         setLoading]       = useState(false);
  const [error,           setError]         = useState(null);
  const [hasMore,         setHasMore]       = useState(true);
  const [searchQuery,     setSearchQuery]   = useState('');
  const [isSearchMode,    setIsSearchMode]  = useState(false);

  const PAGE_SIZE = 8;

  /* refs */
  const allExpertsRef = useRef([]);
  const displayedRef  = useRef([]);
  const fetchSeqRef   = useRef(0);   // increments for each fetch to ignore stale backfills
  const { tagsCacheRef, mediaCacheRef } = useCaches();

  /* helper: place the new slice in state and stop the spinner */
  const updateDisplay = useCallback((list, start = 0) => {
    if (!Array.isArray(list)) {
      setDisplayed([]);
      setLoading(false);
      return;
    }
    const valid = list.filter((e) => (e.isVip ? true : ((e.description || '').trim().length > 0)));
    const slice = valid.slice(start, start + 50);
    setDisplayed(slice);
    displayedRef.current = slice;
    setLoading(false);
  }, []);

  /* ── 📥  FETCHERS (EXPERTS)  ─────────────────────────────────────── */
  const fetchExpertsByIdsInOrder = async (ids, { markVip = false } = {}) => {
    const resolvedIds = (ids || []).map((id) => ID_ALIASES.get(id) ?? id);
    if (!resolvedIds.length) return [];
    const { data, error } = await supabase
      .from('experts')
      .select('*')
      .in('id', resolvedIds);
    if (error) throw error;
    const byId = new Map((data || []).map(e => [Number(e.id), e]));
    return resolvedIds
      .map(id => byId.get(id))
      .filter(Boolean)
      .map(e => normalizeExpert(e, { isVip: markVip }));
  };

  const fetchPriorityExperts = async () => {
    return fetchExpertsByIdsInOrder(PRIORITY_IDS_ALL, { markVip: true });
  };

  /* ── 📥  FETCHERS (TAGS & MEDIA) with CACHE  ─────────────────────── */
  const fetchTagsForIds = async (ids) => {
    const out = {};
    const missing = [];
    for (const id of ids) {
      if (tagsCacheRef.current.has(id)) {
        out[id] = tagsCacheRef.current.get(id);
      } else {
        missing.push(id);
      }
    }
    if (missing.length) {
      await Promise.all(
        missing.map(async (id) => {
          const { data, error } = await supabase
            .from('tags')
            .select('*')
            .eq('tagged_id', id);
          const val = error ? [] : (data ?? []);
          tagsCacheRef.current.set(id, val);
          out[id] = val;
        })
      );
    }
    return out;
  };

  const fetchMediaCounts = async (ids = []) => {
    const out = {};
    const missing = [];
    for (const id of ids) {
      if (mediaCacheRef.current.has(id)) {
        out[id] = mediaCacheRef.current.get(id);
      } else {
        missing.push(id);
      }
    }
    if (missing.length) {
      await Promise.all(
        missing.map(async (id) => {
          const { count, error } = await supabase
            .from('media_items')
            .select('*', { count: 'exact', head: true })
            .eq('owner_id', id)
            .eq('owner_type', 'Id::PromoEntity::Expert');
          const val = !error ? (count ?? 0) : 0;
          mediaCacheRef.current.set(id, val);
          out[id] = val;
        })
      );
    }
    return out;
  };

  /* ── 🪄 BACKFILL (non-blocking Phase B) ─────────────────────────── */
  const backfillMetadata = useCallback(async (seq, baseList) => {
    const ids = baseList.map(e => Number(e.id)).filter(Boolean);
    if (!ids.length) return;

    try {
      const [tags, mediaCounts] = await Promise.all([
        fetchTagsForIds(ids),
        fetchMediaCounts(ids),
      ]);

      // Ignore if a newer fetch started
      if (seq !== fetchSeqRef.current) return;

      // Merge back into the currently displayed list (not stale local copy)
      setAllExperts((prev) => {
        const merged = (prev.length ? prev : baseList).map(e => ({
          ...e,
          tags: tags[e.id] ?? e.tags ?? [],
          mediaCount: mediaCounts[e.id] ?? e.mediaCount ?? 0,
        }));
        allExpertsRef.current = merged;
        return merged;
      });
      updateDisplay(allExpertsRef.current, 0);
    } catch (err) {
      // non-fatal — keep fast paint
      // console.warn('Backfill failed', err);
    }
  }, [updateDisplay]);

  /* ── ⚡ TWO-PHASE FETCHES ────────────────────────────────────────── */
  const fetchAllCategory = async (seq) => {
    // VIPs first (fast)
    const vip = await fetchPriorityExperts();
    const vipIds = vip.map(v => Number(v.id));

    // Similarity (experts only)
    const searchQueryAll = params.get('desc_0') || '';
    const url  = buildSimilarityUrl(searchQueryAll);
    const resp = await fetch(url, {
      headers: {
        apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
    });

    if (!resp.ok) {
      const firstPaint = vip.map(({ sortWeight, ...rest }) => rest);
      return firstPaint;
    }

    const json = await resp.json();
    const ids = (json.data?.results ?? [])
      .map(r => Number(r.model_id))
      .filter(Boolean);

    const nonVipIds = ids.filter(id => !vipIds.includes(id));

    const { data: experts } = nonVipIds.length
      ? await supabase.from('experts').select('*').in('id', nonVipIds)
      : { data: [] };

    const nonVipEnriched = (experts || []).map(e => normalizeExpert(e, { isVip: false }));
    const shuffled = nonVipEnriched.length > 1
      ? shuffleWithSeed(nonVipEnriched, Math.min(150, nonVipEnriched.length))
      : nonVipEnriched.slice();

    const firstPaint = [
      ...vip.map(({ sortWeight, ...rest }) => rest),
      ...shuffled.map(({ sortWeight, ...rest }) => rest),
    ];

    // Kick off Phase B (non-blocking)
    queueMicrotask(() => backfillMetadata(seq, firstPaint));

    return firstPaint;
  };

  const fetchSimilarityCategory = async (seq, cat) => {
    const catVipIds = CATEGORY_VIP_IDS[cat.key] || [];
    const catVip    = await fetchExpertsByIdsInOrder(catVipIds, { markVip: true });

    const url  = buildSimilarityUrl(cat.desc);
    const resp = await fetch(url, { headers: { apikey: process.env.REACT_APP_SUPABASE_ANON_KEY } });
    if (!resp.ok) {
      const firstPaint = catVip.map(({ sortWeight, ...rest }) => rest);
      return firstPaint;
    }

    const json = await resp.json();
    const ids = (json.data?.results ?? [])
      .map(r => Number(r.model_id))
      .filter(Boolean);

    const nonVipIds = ids.filter(id => !catVipIds.includes(id));

    const { data: experts } = nonVipIds.length
      ? await supabase.from('experts').select('*').in('id', nonVipIds)
      : { data: [] };

    const enriched = (experts || []).map(e => normalizeExpert(e, { isVip: false }));
    const shuffled = enriched.length > 1
      ? shuffleWithSeed(enriched, Math.min(enriched.length, 150))
      : enriched.slice();

    const firstPaint = [
      ...catVip.map(({ sortWeight, ...rest }) => rest),
      ...shuffled.map(({ sortWeight, ...rest }) => rest),
    ];

    queueMicrotask(() => backfillMetadata(seq, firstPaint));
    return firstPaint;
  };

  /* ---------- orchestrator ---------- */
  const fetchExperts = useCallback(
    async (cat, append = false) => {
      const seq = ++fetchSeqRef.current; // bump sequence; cancels older backfills
      setLoading(true);
      if (!append) setDisplayed([]);
      setError(null);

      try {
        const list =
          cat.isAllCategory && !append
            ? await fetchAllCategory(seq)
            : await fetchSimilarityCategory(seq, cat);

        // Ignore if a newer fetch started
        if (seq !== fetchSeqRef.current) return;

        setAllExperts((prev) => {
          const combined = append ? [...prev, ...list] : list;
          allExpertsRef.current = combined;
          return combined;
        });

        updateDisplay(
          append ? allExpertsRef.current : list,
          append ? displayedRef.current.length : 0
        );

        // Heuristic hasMore
        setHasMore(list.length >= 100);
      } catch (err) {
        // console.error(err);
        setError(err.message);
        setLoading(false);
      }
    },
    [updateDisplay, backfillMetadata]
  );

  /* ── 🔁  EFFECTS ───────────────────────────────────────────────── */
  useEffect(() => {
    const cat = categories.find(c => c.key === activeCategory);
    if (cat) fetchExperts(cat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory]);

  /* ── ⏭  PAGINATION  ───────────────────────────────────────────── */
  const handleNext = async () => {
    const next = index + PAGE_SIZE;
    if (next < allExperts.length) {
      setIndex(next);
      updateDisplay(allExperts, next);
    } else if (hasMore && !loading) {
      const cat = categories.find(c => c.key === activeCategory);
      if (cat) await fetchExperts(cat, true);
      setIndex(prev => prev + PAGE_SIZE);
      updateDisplay(allExperts, next);
    }
  };
  const handlePrev = () => {
    const prev = Math.max(index - PAGE_SIZE, 0);
    setIndex(prev);
    updateDisplay(allExperts, prev);
  };

  /* ── 🔎 SEARCH (keep semantic order; two-phase backfill) ───────── */
  const handleSearch = async (q = searchQuery) => {
    const query = (q || '').trim();
    if (!query) return;

    const seq = ++fetchSeqRef.current;
    setIsSearchMode(true);
    setLoading(true);
    setDisplayed([]);

    try {
      const url  = buildSimilarityUrl(query);
      const resp = await fetch(url, {
        headers: {
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        if (seq !== fetchSeqRef.current) return;
        setAllExperts([]);
        updateDisplay([], 0);
        setHasMore(false);
        setLoading(false);
        return;
      }

      const json = await resp.json();
      const ids = (json.data?.results ?? [])
        .map((r) => Number(r.model_id))
        .filter(Boolean);

      const { data: experts } = ids.length
        ? await supabase.from('experts').select('*').in('id', ids)
        : { data: [] };

      const byId = new Map((experts || []).map((e) => [Number(e.id), e]));
      const ordered = ids
        .map((id) => byId.get(id))
        .filter(Boolean)
        .map((e) => normalizeExpert(e, { isVip: false }));

      // Phase A: immediate paint (no tags/media)
      const firstPaint = ordered.map(({ sortWeight, ...rest }) => rest);

      if (seq !== fetchSeqRef.current) return;
      setAllExperts(firstPaint);
      updateDisplay(firstPaint, 0);
      setHasMore(false);

      // Phase B backfill
      queueMicrotask(() => backfillMetadata(seq, firstPaint));
    } catch (err) {
      // console.error(err);
      if (seq === fetchSeqRef.current) {
        setError(err.message);
        setAllExperts([]);
        updateDisplay([], 0);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetSearch = () => {
    setSearchQuery('');
    setIsSearchMode(false);
    setActiveCategory(categories[0]?.key ?? 'all');
  };

  /* ── 🧩  RENDER-PROP OUTPUT  ──────────────────────────────────── */
  return children({
    experts:          displayed,
    displayedExperts: displayed,
    loading,
    error,
    activeCategory,
    setActiveCategory,
    categories,
    handleNext,
    handlePrev,
    loadMore: () => fetchExperts(categories.find(c => c.key === activeCategory), true),
    hasMore,
    searchQuery,
    setSearchQuery,
    handleSearch,
    isSearchMode,
    resetSearch,
  });
}
